import { useNavigate, useParams } from 'react-router-dom';
import { useState, useRef } from 'react';
import { AppCard, AppPage } from '../../components/general';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { WaterfallEditor } from './WaterfallEditor';
import { useWaterfalls } from '../../hooks/useWaterfalls';
import { IntegrationType, WaterfallVersionViewModel, WaterfallViewModel } from '../../models/waterfall';

export const WaterfallEditorContainer = () => {
  const { user, isLoading: authIsLoading } = useAuth();
  const navigate = useNavigate();
  const { waterfallId } = useParams<{ waterfallId?: string }>();
  const id = waterfallId ? Number(waterfallId) : undefined;
  const toastRef = useRef<Toast>(null);

  const { waterfallDetailQuery, waterfallsQuery, createWaterfall, updateWaterfall, createVersion, updateVersion, deleteVersion } =
    useWaterfalls(id);

  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  const isLoading = authIsLoading || !user || (id && waterfallDetailQuery.isLoading);

  const onCreate = async ({ name, integrationType }: any) => {
    try {
      const created = await createWaterfall({ name, integrationType });
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Waterfall created successfully',
        life: 3000,
      });
      
      if (created.waterfallId) navigate(`/waterfalls/edit/${created.waterfallId}`);
    } catch (error: any) {
      console.error('Failed to create waterfall:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to create waterfall';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
    }
  };

  const onUpdate = async (data: Partial<WaterfallViewModel>) => {
    if (!id || !waterfallDetailQuery.data) return;

    const existing = waterfallDetailQuery.data;

    // Only send fields that the API accepts
    const payload = {
      waterfallId: id,
      name: data.name ?? existing.name ?? '',
      integrationType: data.integrationType ?? existing.integrationType ?? IntegrationType.None,
      currentVersionId: existing.currentVersionId ?? null,
      disabled: existing.disabled ?? false,
      archived: existing.archived ?? false,
    };

    try {
      await updateWaterfall(payload);
      await waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Waterfall updated successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to update waterfall:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to update waterfall';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });

      throw error;
    }
  };

  const onCreateVersion = async (source: WaterfallVersionViewModel) => {
    if (!id) return;
    
    try {
        const newVersion = await createVersion({
          waterfallId: id,
          executionMode: source.executionMode,
          initialSteps: source.initialSteps,
          initialStepsExecutionMode: source.initialStepsExecutionMode,
          refreshOrderSettings: source.refreshOrderSettings,
        });
      await waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Version ${newVersion.versionNumber} created successfully`,
        life: 3000,
      });
      
      if (newVersion?.waterfallVersionId) setSelectedVersionId(newVersion.waterfallVersionId);
    } catch (error: any) {
      console.error('Failed to create version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to create version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      await waterfallDetailQuery.refetch();
    }
  };

  const onSaveVersion = async (version: WaterfallVersionViewModel) => {
    if (!id) return;

    const payload = {
      waterfallVersionId: version.waterfallVersionId,
      waterfallId: id,
      executionMode: version.executionMode,
      initialSteps: version.initialSteps,
      initialStepsExecutionMode: version.initialStepsExecutionMode,
      refreshOrderSettings: version.refreshOrderSettings,
    };

    try {
      await updateVersion(payload);
      await waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Waterfall version saved successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to save waterfall version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to save waterfall version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      throw error;
    }
  };

  const onSetActiveVersion = async (waterfallId: number, waterfallVersionId: number) => {
    try {
      await updateWaterfall({
        waterfallId,
        name: waterfallDetailQuery.data?.name ?? '',
        integrationType: waterfallDetailQuery.data?.integrationType ?? IntegrationType.None,
        currentVersionId: waterfallVersionId,
      });
      waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Active version updated successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to set active version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to set active version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      throw error;
    }
  };

  const onDeleteVersion = async (versionId: number) => {
    if (!id) return;
    
    try {
      await deleteVersion({ waterfallId: id, waterfallVersionId: versionId });
      waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Version deleted successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to delete version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to delete version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      throw error;
    }
  };

  const onCancel = () => navigate('/waterfalls');

  if (isLoading) {
    return (
      <AppPage title="Waterfall Editor" breadcrumbs="Admin - Waterfalls">
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading Waterfall...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />
      <WaterfallEditor
        waterfall={waterfallDetailQuery.data ?? null}
        onSubmit={id ? onUpdate : onCreate}
        onCancel={onCancel}
        onCreateVersion={onCreateVersion}
        onSetActiveVersion={onSetActiveVersion}
        onSaveVersion={onSaveVersion}
        onDeleteVersion={onDeleteVersion}
        initialSelectedVersion={selectedVersionId}
        allWaterfalls={waterfallsQuery.data ?? []}
      />
    </>
  );
};
